import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PermissionsStyles from '../../../../components/Permissions/styles/PermissionsStyles';
import AgentsPicker from './AgentsPicker';
import ManagerPicker from './ManagerPicker';
import BranchesPicker from './BranchesPicker';
import useAgentManagerForm from './useAgentManagerForm';

const DialogButtons = ({ isEdit, saving, canSubmit, onCancel }) => (
  <DialogActions sx={PermissionsStyles.dialogActions}>
    <Button onClick={onCancel} variant="outlined" sx={PermissionsStyles.buttonOutlined} disabled={saving}>
      ביטול
    </Button>
    <Button
      type="submit"
      variant="contained"
      disabled={!canSubmit || saving}
      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
      sx={PermissionsStyles.buttonContained}
    >
      {isEdit ? 'שמור' : 'הוסף'}
    </Button>
  </DialogActions>
);

const AgentManagerFields = ({ form, isEdit, users, agentsLoading, branchOptions }) => (
  <Grid container spacing={3} direction="column">
    <Grid item xs={12}>
      <AgentsPicker
        options={form.availableAgents}
        loading={agentsLoading}
        value={form.fields.selectedAgents}
        onChange={form.setters.setSelectedAgents}
        locked={isEdit}
      />
    </Grid>
    <ManagerPicker users={users} value={form.fields.manager} onChange={form.setters.setManager} />
    <Grid item xs={12}>
      <BranchesPicker
        options={branchOptions}
        allSelected={form.fields.allBranchesSelected}
        onAllSelectedChange={form.setters.setAllBranchesSelected}
        value={form.fields.branches}
        onChange={form.setters.setBranches}
      />
    </Grid>
    {form.otherManagers.length > 0 && (
      <Grid item xs={12}>
        <Alert severity="info">משויך גם למנהל אחר: {form.otherManagers.join(', ')}</Alert>
      </Grid>
    )}
  </Grid>
);

// Add (several agents for one manager) / edit (one row) dialog — same look as AddUserForm.
const AgentManagerDialog = ({ initial, rows, users, agentOptions, agentsLoading, branchOptions, onSave, onCancel }) => {
  const isEdit = !!initial;
  const form = useAgentManagerForm({ initial, rows, users, agentOptions });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave(form.toPayload());
    } catch (err) {
      setError(err?.msg || 'שגיאה בשמירה');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onCancel} PaperProps={{ sx: PermissionsStyles.dialogPaperLarge }} maxWidth="md">
      <div style={PermissionsStyles.containerLarge}>
        <DialogTitle sx={PermissionsStyles.dialogTitle}>
          <PersonAddIcon sx={PermissionsStyles.icon} />
          <Typography variant="h5" component="div" sx={PermissionsStyles.dialogTitleText}>
            {isEdit ? 'עריכת סוכן' : 'הוספת סוכן'}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={PermissionsStyles.dialogContent}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <AgentManagerFields
              form={form}
              isEdit={isEdit}
              users={users}
              agentsLoading={agentsLoading}
              branchOptions={branchOptions}
            />
          </DialogContent>
          <DialogButtons isEdit={isEdit} saving={saving} canSubmit={form.isValid} onCancel={onCancel} />
        </form>
      </div>
    </Dialog>
  );
};

export default AgentManagerDialog;
